/**
 * Deep links and sign-in tokens — the plain half.
 *
 * This covers three things that arrive from OUTSIDE the running app and can
 * each change who is signed in:
 *
 *  - a **deep link**: the operating system hands the app a URL because the user
 *    tapped a link to us somewhere else (a Universal Link on iOS, an App Link
 *    on Android). It may name a page to open, or it may carry a sign-in token.
 *  - a **sign-in token**: a short-lived Firebase custom token in the address
 *    bar, which is what a "magic link" email actually delivers. Signing in with
 *    it is what makes the link work.
 *  - a **purchase claim token**: handed out when somebody buys before they have
 *    an account. Exchanging it for a sign-in token is how the purchase reaches
 *    the right account.
 *
 * There is NO Angular and no Firebase in this file. The only browser API it names
 * itself is `atob` (which node provides too), and every module it imports is safe
 * to load outside a browser: `discourse-return-host`, for the community sign-in
 * parser, which touches `document` only inside functions this file never calls;
 * `@cf-shared/error-describe`, which is plain TypeScript; and `utils/url-parser`,
 * which is plain text parsing with no browser API at all.
 * Everything else outside it arrives through `SignInFlowEnvironment` below.
 * That is what lets the node unit-test runner load it at all — Angular, Firebase,
 * alerts and navigation do not exist there — and it is the reason this half exists
 * separately from `DeepLinkSignInService`, which supplies the real ones.
 *
 * Lifted out of `AppComponent` with no behavior change (fourth and final slice
 * of deferred finding df-7b66acbeb341). The boot sequence still decides WHEN
 * each of these runs; see design-docs/boot-sequence.md.
 */

import { describeErrorForLog } from '@cf-shared/error-describe';
import { parseSsoReturn } from 'services/discourse-return-host';
import { parseUrlToPages } from 'utils/url-parser';
import { AccountHintParse, readAccountHintFromUrl } from 'services/account-hint';
import { isClientDeepLinkPage } from 'services/coach-manage-deep-link';

/**
 * Why a sign-in token did not work, in the three shapes the login page tells
 * apart. The values match `SignInTokenError` in services/external-flow.service
 * exactly; that enum is not imported here because its module pulls in Angular.
 */
export type SignInTokenFailureKind = 'expired' | 'network' | 'other';

/**
 * How long to wait for a sign-in with a link token before letting the app get on
 * with itself (df-993c16916483).
 *
 * Firebase's `signInWithCustomToken` puts no limit of its own on that call, and
 * the boot sequence awaits this work before it shows anything at all — so a call
 * that neither succeeds nor fails used to park the whole boot for ever, leaving
 * the person holding a magic link staring at the splash screen. The only sign it
 * had happened was a canary about something else firing three minutes later.
 *
 * 15 s sits well above a slow-but-working sign-in on a phone, and well below the
 * 180 s boot read-priority failsafe. It is longer than the 8 s of
 * BOOT_AUTH_SETTLE_TIMEOUT_MS in auth.service.ts because that bound covers a
 * local session restore and this one covers a full network round trip.
 *
 * Reaching it never cancels the sign-in — see `signInWithTokenBoundedAsync`.
 *
 * Wall time, checked in #6096 (2026-10-01): Sentry held no report of it at all. Reaching it
 * after a freeze costs a glimpse of the sign-in screen, because a late answer still signs the
 * person in.
 */
export const SIGN_IN_TOKEN_WAIT_LIMIT_MS = 15_000;

/**
 * How long the boot waits for the operating system to say which link started the
 * app, before carrying on without one.
 *
 * Reading the link is now on the boot's critical path, and
 * design-docs/boot-sequence.md allows no boot-critical wait without a bound. The
 * call only reads a string the native side stored when the app window was
 * created — no network, no disk — so a healthy answer takes milliseconds and 2 s
 * is far above it. Reaching the limit is reported and costs nothing else: the
 * ordinary launch-link handling later in the boot chains onto the same promise
 * without waiting for it, so a late answer is still acted on there
 * (df-febb6fcfb9e9).
 */
export const LAUNCH_URL_WAIT_LIMIT_MS = 2_000;

/**
 * How long the first-launch install-referrer check may hold up the boot
 * (df-febb6fcfb9e9). The Play referrer service is a cross-process call, so it gets
 * more room than the launch link, but a service that never answers must not keep
 * the person off the login page. A late answer is still acted on, see
 * `exchangeLateInstallReferrerClaimTokenAsync`. Defined here rather than in
 * install-referrer.service.ts, which the boot loads lazily so the plugin stays
 * out of the web bundle.
 */
export const INSTALL_REFERRER_WAIT_LIMIT_MS = 5_000;

/** Whether a bounded sign-in finished, or is simply still going. */
export enum SignInTokenWaitOutcome {
  /** Firebase answered and the person is signed in. */
  SignedIn = 'signed-in',
  /**
   * The wait limit was reached first. The sign-in is STILL RUNNING — it was not
   * cancelled — so it may yet succeed and sign the person in.
   */
  StillWaiting = 'still-waiting',
}

/**
 * Which sign-in a wait limit was reached on, carried into the report.
 *
 * The first two are sign-ins the BOOT waits on: nothing restarts the app
 * afterwards, so a late answer still arrives somewhere. The two account-switch
 * sites are different — a switch is only safe once the app restarts, so they
 * hand the bound a restart to run if the answer comes late (df-28856ee737e9).
 */
export type SignInTokenWaitSite =
  /** The sign-in token the boot found in the address bar. */
  | 'boot'
  /** The sign-in token a purchase claim token was traded for. */
  | 'claim-token'
  /** The sign-in token of a deferred account switch the person accepted. */
  | 'account-switch'
  /** The sign-in token a claim token was traded for when that switch's own link had expired. */
  | 'account-switch-claim-token';

/** How trading a purchase claim token for a sign-in ended, as far as the caller needs to know. */
export enum ClaimTokenSignInOutcome {
  /** The sign-in finished and the person is signed in. */
  SignedIn = 'signed-in',
  /** The wait limit was reached first. The sign-in is still running and may yet succeed. */
  StillWaiting = 'still-waiting',
  /** The exchange or the sign-in failed. It has already been reported where that applies. */
  NotSignedIn = 'not-signed-in',
}

/**
 * Session-storage key holding the cold-start launch link this web-view session
 * has already acted on (#4994).
 *
 * It lives in this half, rather than beside the storage code in
 * `deep-link-signin.service.ts`, so that it can be imported without pulling in
 * Angular. The sign-out wipe is what needs that: `app/reload-teardown.ts` reads
 * this key so it can carry it across the wipe instead of destroying it, and that
 * module is deliberately Angular-free so `reload-teardown.test.ts` can run it
 * under node. Move this constant into the service and the wipe stops loading
 * outside a browser.
 */
export const HANDLED_LAUNCH_URL_KEY = 'handled_launch_url';

/** What a loading spinner has to be able to do for this code. */
export interface DismissableLoader {
  dismissAsync(): Promise<void>;
}

/** The account a Firebase sign-in token names, as far as we can read it. */
export interface DecodedSignInToken {
  uid?: string;
  claims?: { email?: string };
}

/**
 * Which call site asked to exchange a purchase claim token.
 *
 * It decides whether a failed exchange is reported. Every site but the
 * install-referrer guess ran because a token was really present, so a failure
 * there means a paying customer's purchase did not reach their account — that is
 * reported. The install-referrer site is a best-effort cold-start guess where a
 * failure means nothing, so it stays silent. The value is also carried into the
 * telemetry context, so the reported sites can be told apart.
 */
export type ClaimTokenExchangeSource =
  /** A claim token was in the deep link and there was no sign-in token. */
  | 'link-claim-only'
  /** An expired sign-in token fell back to the claim token during boot. */
  | 'signin-token-expired-fallback'
  /** An expired sign-in token fell back to the claim token during an account switch. */
  | 'account-switch-expired-fallback'
  /** The first-launch Android install-referrer guess. Failures here are not reported. */
  | 'install-referrer';

/** An account switch that was noticed during boot and deferred until the UI exists. */
export interface PendingAccountSwitch {
  signInToken: string;
  claimToken?: string;
  tokenEmail: string;
  currentEmail: string;
}

/**
 * Everything outside this file that a deep link or a sign-in token touches.
 *
 * It is one interface rather than a list of injected services so a unit test
 * can drive the whole flow with a single plain object.
 */
export interface SignInFlowEnvironment {
  /** The signed-in account right now, or null when nobody is signed in. */
  currentUser(): { uid: string; email: string | null } | null;

  /** Sign in with a Firebase custom token. Throws `Error('EXPIRED_TOKEN')` when it has expired. */
  signInWithTokenAsync(signInToken: string): Promise<void>;

  /** Resolve after `ms` milliseconds. Injected so a test can drive the wait limit without waiting. */
  sleep(ms: number): Promise<void>;

  /**
   * Let the app carry on as nobody-signed-in, even though a sign-in token is
   * still in play (df-993c16916483).
   *
   * A boot that finds a sign-in token holds back the "nobody is signed in"
   * signal, because that signal is what shows the login page and the token is
   * meant to make a login page unnecessary. That hold has to be released by
   * hand, and until it is, the app has no screen to show — so releasing the boot
   * without this would swap a frozen splash for a frozen splash.
   *
   * A sign-in that lands later still wins: Firebase's own listener emits the
   * signed-in user through the same pipeline and the app routes the person in.
   */
  proceedAsSignedOutAsync(): Promise<void>;

  /** Trade a purchase claim token for a sign-in token on the server. */
  exchangeClaimTokenAsync(claimToken: string): Promise<{ token?: string; error?: string }>;

  /**
   * Email the user a fresh magic link. The result says whether the server
   * REFUSED to send one because several accounts hold that address (#4505) — in
   * which case no email went out and the caller must not claim otherwise.
   */
  resendMagicLinkAsync(email: string): Promise<{ accountAmbiguous?: boolean }>;

  /**
   * True when this error means the network was unreachable, rather than the
   * token being wrong. Passed in because deciding it needs Firebase's own error
   * class, which this file deliberately does not import.
   */
  isNetworkAuthError(err: unknown): boolean;

  /** Record why a sign-in token failed, so the login page can explain it. */
  setSignInTokenFailure(kind: SignInTokenFailureKind): void;

  /** Park an account switch until the UI is ready to ask about it. */
  setPendingAccountSwitch(data: PendingAccountSwitch): void;

  /** Ask the user whether to switch accounts. Resolves false when they decline. */
  confirmAccountSwitchAsync(currentEmail: string, newEmail: string): Promise<boolean>;

  /** Tell the user their link had expired and a fresh one is on its way. */
  showLinkExpiredAlertAsync(email: string): Promise<void>;

  /**
   * Tell the user we could not send a link because several accounts hold their
   * address, so they should contact support (#4505).
   */
  showAmbiguousAccountAlertAsync(): Promise<void>;

  /**
   * Tell the user their link had expired AND that sending a fresh one did not
   * work, so they should try again.
   *
   * This is the third of the three things that can happen when we try to email a
   * replacement link, and it is separate from the other two on purpose: one says
   * a link is on its way, one says we refused to send, and this one says we
   * tried and failed. Telling somebody a link was sent when the send threw would
   * leave them waiting for an email that is never coming.
   */
  showLinkResendFailedAlertAsync(email: string): Promise<void>;

  /**
   * Tell the user a switch to `newEmail` did not finish in time, so they are
   * still in the account they were in (df-28856ee737e9).
   */
  showAccountSwitchUnfinishedAlertAsync(newEmail: string): Promise<void>;

  /** Put a "signing in" spinner on screen. */
  presentSigningInLoaderAsync(): Promise<DismissableLoader>;

  /** Restart the app at its root page, which is how a completed switch takes effect. */
  reloadAppAtRoot(): void;

  /** The page a deep-link path names, or undefined when we do not recognise it. */
  resolvePageName(path: string): string | undefined;

  /** Open one of our own pages, with the parameters the link carried (#5124). */
  navigateToPageAsync(pageName: string, params?: Record<string, string>): Promise<void>;

  /** Hand a URL to the system browser, outside the app. */
  openExternalLinkAsync(url: string): Promise<void>;

  /**
   * Pass on the account hint a link carried, if any (#5895). The account-hint service
   * decides when it is checked; this flow only reads it out of the link.
   */
  noteAccountHint(parse: AccountHintParse): void;

  /**
   * Finish signing this person in to the community forum (#4688).
   *
   * Given the pair the forum handed back, this asks our server to sign a reply
   * and then sends the person on to the forum with it. On a phone that last hop
   * has to leave the app and land in the SAME browser that started the sign-in,
   * because the forum remembers the request in that browser's session.
   */
  completeCommunityLoginAsync(sso: string, sig: string): Promise<void>;

  /**
   * Enter one of this coach's client accounts, and open `page` there when the
   * link named one (#6433). This restarts the app, as tapping a coach push
   * notification does. When the link is what started the app, the account is
   * entered once the app has finished starting.
   */
  enterCoachClientAsync(clientUid: string, page?: string): Promise<void>;

  /**
   * Open a page of the signed-in person's own account that a notification link
   * named (#6433). When the link is what started the app, the page is opened
   * once the app has finished starting.
   */
  openClientPageAsync(page: string): Promise<void>;

  /** Point this web view at a path on our own site, which reloads the app. */
  navigateWithinAppTo(pathAndQuery: string): void;

  /**
   * Hand a cold-start link's boot parameters to the boot that is ALREADY
   * running, instead of reloading so a fresh boot can read them.
   *
   * Writes `pathAndQuery` into the address bar without navigating, then re-reads
   * the parameters out of it. Only the path and the query are ever passed: the
   * launch link names the public site while the web view serves from a local
   * address of its own. See design-docs/purchase-deep-linking.md, "A cold-start
   * link is read in place".
   */
  adoptBootParamsInPlace(pathAndQuery: string): void;

  /**
   * Whether parameters adopted from a launch link are still waiting to be read —
   * a sign-in token, a purchase claim token or a checkout result the boot has not
   * consumed yet.
   *
   * Asked rather than remembered, on purpose. The two kinds of parameter are
   * consumed at very different points in the boot, so a stored "the boot has
   * finished with them" flag was right for the sign-in token and wrong for a
   * checkout result, and a boot that threw could leave it set for ever. Asking
   * makes both mistakes impossible.
   */
  adoptedBootParamsStillUnread(): boolean;

  /**
   * The cold-start launch link this web-view session has already acted on, or
   * undefined when it has acted on none. A reader that cannot answer must answer
   * undefined, so a genuine deep link still works.
   */
  readHandledLaunchUrl(): string | undefined;

  /** Remember that this cold-start launch link has been acted on. */
  markLaunchUrlHandled(url: string): void;

  /**
   * Report a condition somebody may eventually need to act on.
   *
   * `context` is deliberately untyped: two of the three call sites below hand
   * over the caught error itself, exactly as the original code did, and the
   * third hands over a small object built from it.
   */
  reportProblem(name: string, context: unknown): void;
}

/**
 * Read the account out of a Firebase custom token WITHOUT checking its
 * signature.
 *
 * That is safe for what it is used for and only for that: deciding whether the
 * token names the account already signed in, so we can skip asking the user a
 * pointless "switch account?" question. Nothing is granted on the strength of
 * this — the real check happens on Firebase's servers when the token is
 * actually used.
 *
 * A token we cannot read comes back empty. Be aware of what the caller then does
 * with that: `processSignInTokenAsync` below does NOTHING in that case for somebody
 * who is already signed in — it does not sign them in, and it does not ask about
 * switching accounts. It reports it, so we can tell how often a damaged link reaches
 * somebody.
 *
 * That is a decision, not an oversight (#4921, 2026-09-04): the report had fired zero
 * times in production, so no message and no translation work was spent on it. The
 * evidence, and what the zero does not prove, are in
 * design-docs/purchase-deep-linking.md, "A magic link a signed-in person cannot use is
 * reported and otherwise left alone". #5254 re-checks the counter later.
 */
export function decodeSignInTokenPayload(token: string): DecodedSignInToken {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    const payload = JSON.parse(atob(parts[1]));
    return { uid: payload.uid, claims: payload.claims };
  } catch {
    // an unreadable token answers "no account id", which the caller reports with the token's shape
    return {};
  }
}

/**
 * What became of an attempt to email somebody a replacement sign-in link.
 *
 * Each value has exactly one message that goes with it, and the three messages
 * make different promises — so picking the wrong one tells the person something
 * that did not happen.
 */
export enum LinkResendOutcome {
  /** The server accepted it, so an email is on its way. */
  Sent = 'sent',
  /** The server refused to send, because several accounts hold that address (#4505). */
  AccountAmbiguous = 'account-ambiguous',
  /** The attempt itself failed, so no email was sent and we do not know why. */
  SendFailed = 'send-failed',
}

/** What a deep link turns out to be asking for. */
export type DeepLinkAction =
  /** It carries boot parameters, so the whole app must reload to process them. */
  | { kind: 'reload-with-params'; pathAndQuery: string }
  /**
   * It names one of our pages, along with any parameters the link wrote into
   * the path (`:name~value` after the page segment) — the recipe id on a shared
   * recipe link, for instance. Absent when the link carried none.
   */
  | { kind: 'open-page'; pageName: string; params?: Record<string, string> }
  /**
   * It is the forum sending somebody back from asking to sign in to the
   * community, and the app has to finish that sign-in itself (#4688).
   */
  | { kind: 'complete-community-login'; sso: string; sig: string }
  /**
   * It is the button in a coach's notification email: enter that client's
   * account, and open `page` there when the link names one (#6433).
   */
  | { kind: 'enter-coach-client'; clientUid: string; page?: string }
  /**
   * It is the button in a client's own notification email, naming a page of
   * their own account in the `segment:key~value` form (#6433). Only a page
   * `isClientDeepLinkPage` accepts ever gets this far.
   */
  | { kind: 'open-client-page'; page: string }
  /** We do not recognise the path — it belongs to the website, not the app. */
  | { kind: 'open-external' }
  /** Nothing to do (bare domain, no path). */
  | { kind: 'ignore' };

/**
 * Work out what a deep link is asking for.
 *
 * The reload branch comes first and swallows everything else: a link carrying
 * `signInToken` or `checkout_result` has to be handled by the ordinary boot
 * path, which reads those parameters out of the address bar. Navigating in
 * place would leave them unread.
 */
export function decideDeepLinkAction(
  url: URL,
  resolvePageName: (path: string) => string | undefined,
): DeepLinkAction {
  if (url.searchParams.has('signInToken') || url.searchParams.has('checkout_result')) {
    return { kind: 'reload-with-params', pathAndQuery: url.pathname + url.search };
  }

  // #4688: the community sign-in comes back to a bare `/` on our own site with
  // the pair in the query, and the phone claims that address, so the operating
  // system hands it here instead of letting the browser follow it. This MUST sit
  // above the bare-path check below, which would otherwise answer "do nothing"
  // and end the sign-in in silence — which is exactly what it did before this
  // arm existed. It sits below the reload arm so a sign-in token still wins.
  const ssoReturn = parseSsoReturn(url.search);
  if (ssoReturn) {
    return { kind: 'complete-community-login', sso: ssoReturn.sso, sig: ssoReturn.sig };
  }

  const path = url.pathname;
  if (!path || path === '/') {
    // #6433: the buttons in our notification emails all point at a bare `/` and say where to
    // go in the query. Like the community return above, these MUST be read before the "do
    // nothing" answer below, which is what swallowed them: the app came forward on the page
    // the person was already on. The coach arm comes first, because a coach link's `page`
    // names a page of the CLIENT's account and must never be opened in the coach's own.
    const clientUid = url.searchParams.get('coachManage');
    const page = url.searchParams.get('page');
    if (clientUid) {
      return page ? { kind: 'enter-coach-client', clientUid, page } : { kind: 'enter-coach-client', clientUid };
    }
    if (isClientDeepLinkPage(page)) {
      return { kind: 'open-client-page', page: page! };
    }
    return { kind: 'ignore' };
  }

  // #4627: a referral invite is handed back out to a browser, and this arm MUST
  // stay above the page lookup below — `/invite` is a registered page segment, so
  // the lookup would match it first and this would become dead code that never
  // runs, with nothing to warn you. Two tests in deep-link-signin.test.ts are the
  // only thing that catches that. Why the app must not keep the link, and why the
  // bounce is on both platforms rather than Android alone, are in
  // design-docs/referral-program.md.
  if (isReferralInvitePath(path)) return { kind: 'open-external' };

  const pageName = resolvePageName(path);
  if (!pageName) return { kind: 'open-external' };

  // #5124: the page NAME alone is not enough. A shared recipe link is
  // `/food:id~food%2F%2BUDR-<id>:mode~view`, and opening EditFoodPage without
  // that id gives the person the empty "New Food" form instead of the recipe.
  // The parameters belong to the LAST segment of the path, which is the same
  // segment `resolvePageName` took the name from — read both the same way so
  // they can never come from different segments.
  const pages = parseUrlToPages(path);
  const params = pages.length ? pages[pages.length - 1].params : {};
  // no parameters is passed on as nothing, so a plain link navigates exactly as before
  return Object.keys(params).length
    ? { kind: 'open-page', pageName, params }
    : { kind: 'open-page', pageName };
}

/** Whether the link that started the app should still be acted on. */
export type LaunchUrlDecision =
  | { kind: 'handle' }
  /** Already acted on in this web-view session. */
  | { kind: 'skip'; reason: 'already-handled' }
  /** The web view is already showing the address it would be sent to. */
  | { kind: 'skip'; reason: 'already-at-that-address' };

/**
 * Decide whether the link that STARTED the app should still be acted on (#4994).
 *
 * Two separate rules, because each covers a case the other does not: the app has
 * already acted on this exact link in this session — matched on the whole address,
 * which is what `markLaunchUrlHandled` stores — or the web view is already showing
 * the address the link would send it to, for which only the path and the query are
 * compared, never the whole address.
 *
 * Why any of that is needed, and why neither rule can be dropped, is in
 * design-docs/purchase-deep-linking.md, "The launch link is acted on ONCE per web-view session".
 */
export function decideLaunchUrlHandling(
  launchUrl: URL,
  currentPathAndQuery: string,
  handledLaunchUrl: string | undefined,
): LaunchUrlDecision {
  if (handledLaunchUrl === launchUrl.toString()) {
    return { kind: 'skip', reason: 'already-handled' };
  }

  if (launchUrl.pathname + launchUrl.search === currentPathAndQuery) {
    return { kind: 'skip', reason: 'already-at-that-address' };
  }

  return { kind: 'handle' };
}

/**
 * Whether a deep link's path is a referral invite.
 *
 * Matched exactly, plus anything below it. That is deliberately NARROWER than
 * the `/invite*` glob in the iOS association file, which would also match a
 * path like `/invitations`: an association file cannot express "this path and
 * its children" any more precisely, whereas here we can, and sending an
 * unrelated page out to a browser would break it with no way to tell why.
 * Nothing else in the app starts with those letters today, so the two agree in
 * practice; if a path ever did, this is the side that stays correct.
 */
function isReferralInvitePath(path: string): boolean {
  return path === '/invite' || path.startsWith('/invite/');
}

/**
 * Deep-link and sign-in-token handling, lifted out of `AppComponent` unchanged.
 *
 * This is the plain half of `DeepLinkSignInService`, which supplies the real
 * Firebase, alerts, navigation and telemetry.
 */
export class DeepLinkSignInFlow {
  /**
   * The cold-start launch link whose boot parameters THIS boot wrote into the
   * address bar.
   *
   * It exists for one case, and iOS is the only platform that produces it: the
   * operating system there hands a cold-start link to the app twice, so without
   * this the second delivery would reload the web view moments after the first
   * handed the parameters to the boot. In memory rather than `sessionStorage`
   * because it describes the boot happening right now and must die with the page.
   * Why iOS delivers twice, and why a re-tap must still reload, are in
   * design-docs/purchase-deep-linking.md, "A cold-start link is read in place".
   */
  private paramsAdoptedFromLaunchUrl: string | undefined;

  constructor(private env: SignInFlowEnvironment) {}

  /**
   * Sign in with a link token, but stop WAITING after SIGN_IN_TOKEN_WAIT_LIMIT_MS
   * (df-993c16916483).
   *
   * The two sign-ins the BOOT waits on go through here — the token in the
   * address bar, and the one a purchase claim token is traded for — and so do
   * both sign-ins of a deferred account switch (df-28856ee737e9).
   *
   * `onLateSuccess` runs only when the sign-in succeeds AFTER the limit was
   * reached. The account switch passes the app restart: a switch only becomes
   * safe once the app restarts, and the caller that would normally restart it
   * stopped waiting long ago.
   *
   * It stops waiting; it does NOT cancel. The sign-in keeps running, so a slow
   * answer still signs the person in through Firebase's own listener — which is
   * what makes reaching the limit cheap even when the app was merely asleep in
   * the background rather than genuinely stuck.
   *
   * A sign-in that FAILS before the limit is rethrown unchanged, so every
   * existing `catch` around these calls keeps behaving exactly as before. One
   * that fails AFTER it cannot be rethrown — the caller left long ago — so it is
   * reported instead. Reporting it is the point rather than tidiness: it is what
   * says whether a limit that was reached ended in a late success (nothing was
   * wrong, the phone was probably just asleep) or in a real failure.
   *
   * Both handlers are attached at once rather than awaited, so a rejection
   * arriving after the limit is always observed and can never surface as an
   * unhandled rejection.
   */
  private async signInWithTokenBoundedAsync(
    signInToken: string,
    site: SignInTokenWaitSite,
    onLateSuccess?: () => void,
  ): Promise<SignInTokenWaitOutcome> {
    let outcome = SignInTokenWaitOutcome.StillWaiting;
    let failure: unknown;
    let failed = false;
    let limitReached = false;

    const signIn = this.env.signInWithTokenAsync(signInToken).then(
      () => {
        outcome = SignInTokenWaitOutcome.SignedIn;
        if (limitReached) {
          console.log(`Auth: sign-in using token succeeded after its wait limit had passed (${site})`);
          onLateSuccess?.();
        }
      },
      err => {
        failed = true;
        failure = err;
        if (limitReached) {
          // `describeErrorForLog`, not the raw error: an Error's message and
          // stack are non-enumerable, so a bare `err` in this context object
          // serialises to `{}` — and WHY it failed is the whole point of this
          // report. It is the log-side describer because this never crosses the
          // wire to a client.
          const described = describeErrorForLog(err);
          console.log(`Auth: sign-in using token failed after its wait limit had passed (${site}): ${described}`);
          this.env.reportProblem('Sign-in token failed after its wait limit', { site, error: described });
        }
      },
    );

    await Promise.race([signIn, this.env.sleep(SIGN_IN_TOKEN_WAIT_LIMIT_MS)]);

    if (failed) throw failure;

    if (outcome === SignInTokenWaitOutcome.StillWaiting) {
      limitReached = true;
      console.log(`Auth: sign-in using token has not answered within ${SIGN_IN_TOKEN_WAIT_LIMIT_MS}ms — carrying on without it (${site})`);
      this.env.reportProblem('Sign-in token wait hit its limit', {
        site,
        limit_ms: SIGN_IN_TOKEN_WAIT_LIMIT_MS,
      });
    }
    return outcome;
  }

  /**
   * Handle a sign-in token found during boot.
   *
   * This runs during boot rather than on the login page on purpose. The boot
   * path holds back the "nobody is signed in" signal while an external flow is
   * pending, and that signal is what would show the login page — so a token
   * only processed by the login page could never be processed at all.
   *
   * Three cases: nobody signed in (just use it), the token names the account
   * already signed in (ignore it), or it names a different account (park the
   * question until the UI can ask it).
   */
  async processSignInTokenAsync(signInToken: string, claimToken?: string): Promise<void> {
    const currentUser = this.env.currentUser();

    if (!currentUser) {
      // not signed in — just sign in with the token
      try {
        const outcome = await this.signInWithTokenBoundedAsync(signInToken, 'boot');
        if (outcome === SignInTokenWaitOutcome.StillWaiting) {
          // the sign-in is still going, but the boot must not wait for it any
          // longer. Release the held-back "nobody is signed in" signal so the
          // app has a screen to show, and leave the sign-in to finish (or not)
          // on its own — if it lands, Firebase's listener signs the person in
          // from wherever they are.
          //
          // Deliberately NO setSignInTokenFailure here: that routes the login
          // page through otlError(), which tells the person their link has
          // expired. We do not know that, and it would be flatly wrong for a
          // sign-in that succeeds a second later.
          await this.env.proceedAsSignedOutAsync();
          return;
        }
      } catch (e) {
        const isExpired = e instanceof Error && e.message === 'EXPIRED_TOKEN';
        const isNetwork = this.env.isNetworkAuthError(e);

        // if token expired but we have a claim token, try exchanging it
        if (isExpired && claimToken) {
          console.log('App: signInToken expired, trying claimToken fallback');
          await this.exchangeClaimTokenAndSignInAsync(claimToken, 'signin-token-expired-fallback');
          return;
        }

        // if expired and no claimToken, fall through to the existing login page error handling
        // (the login page shows the expired token message and lets user request a new link)

        this.env.setSignInTokenFailure(
          isExpired ? 'expired' :
          isNetwork ? 'network' :
          'other'
        );
        if (!isExpired && !isNetwork) {
          this.env.reportProblem('OTP sign-in failed during boot', e);
        }
      }
      return;
    }

    // user IS signed in — check if the token is for a different account
    const { uid: tokenUid, claims } = decodeSignInTokenPayload(signInToken);

    // if we could not read the token at all — NOT the same as "it names the
    // account you are already signed into", which is why they are separate
    // branches: every token we create carries an account id. Doing nothing beyond
    // reporting is the decision #4921 reached, and so is not trying the claim token
    // below: it names whichever account owns the purchase, which may not be this one.
    // See design-docs/purchase-deep-linking.md.
    if (!tokenUid) {
      // report the token's SHAPE and never its content — it is a credential.
      // Three dot-separated sections means the middle section was unreadable;
      // any other count means the link did not arrive whole.
      //
      // The conversion keeps this from throwing during boot: the client is not
      // built with strictNullChecks, so the token can be absent despite its type.
      const token = typeof signInToken === 'string' ? signInToken : '';
      this.env.reportProblem('Sign-in token could not be read during boot', {
        sections: token.split('.').length,
        tokenLength: token.length,
        hadClaimToken: !!claimToken,
      });
      console.log('App: signInToken could not be read, ignoring');
      return;
    }

    if (tokenUid === currentUser.uid) {
      // same account (possibly linked by Firebase) — no-op
      console.log('App: signInToken is for current user, ignoring');
      return;
    }

    // different account — defer the switch prompt until after UI is ready
    // (the alert needs a host element that is not registered during boot)
    const tokenEmail = claims?.email || '';
    const currentEmail = currentUser.email || '';
    console.log(`App: signInToken is for different user (${tokenEmail} vs ${currentEmail})`);
    this.env.setPendingAccountSwitch({ signInToken, claimToken, tokenEmail, currentEmail });
  }

  /**
   * Ask about, and carry out, an account switch that boot deferred.
   *
   * The spinner is deliberately left ON SCREEN whenever the app is about to
   * restart — dismissing it would show the old account's screen for an instant
   * before the reload wipes it. It is dismissed on the paths that stay on this
   * page: a failure with no fallback left — which includes a claim-token
   * fallback that was tried and did not sign the person in — and a sign-in that
   * did not answer within its wait limit.
   *
   * Both sign-ins here are bounded, and a late success runs the restart itself
   * (df-28856ee737e9) — why, in design-docs/boot-sequence.md, "The deferred
   * account switch is bounded as well".
   */
  async handlePendingAccountSwitchAsync(data: PendingAccountSwitch): Promise<void> {
    const shouldSwitch = await this.env.confirmAccountSwitchAsync(data.currentEmail, data.tokenEmail);
    if (!shouldSwitch) return;

    const loader = await this.env.presentSigningInLoaderAsync();
    const restartIntoNewAccount = () => this.env.reloadAppAtRoot();

    try {
      const outcome = await this.signInWithTokenBoundedAsync(data.signInToken, 'account-switch', restartIntoNewAccount);
      if (outcome === SignInTokenWaitOutcome.StillWaiting) {
        await this.giveUpWaitingForAccountSwitchAsync(loader, data.tokenEmail);
        return;
      }
      this.env.reloadAppAtRoot();
    } catch (e) {
      const isExpired = e instanceof Error && e.message === 'EXPIRED_TOKEN';
      // if expired, try claim token fallback
      if (isExpired && data.claimToken) {
        console.log('App: signInToken expired during switch, trying claimToken');
        const fallback = await this.exchangeClaimTokenAndSignInAsync(
          data.claimToken, 'account-switch-expired-fallback', restartIntoNewAccount);
        if (fallback === ClaimTokenSignInOutcome.SignedIn) {
          this.env.reloadAppAtRoot();
          return;
        }
        if (fallback === ClaimTokenSignInOutcome.StillWaiting) {
          await this.giveUpWaitingForAccountSwitchAsync(loader, data.tokenEmail);
          return;
        }
        // the fallback failed and has already reported why. Restarting now would
        // drop the person back into the account they were leaving with nothing
        // said, so treat it as an expired link with no fallback: stay here and
        // tell them, with a fresh link.
      }
      await loader.dismissAsync();
      if (isExpired && data.tokenEmail) {
        await this.replaceExpiredLinkAndSayWhatHappenedAsync(data.tokenEmail);
      } else if (!isExpired) {
        this.env.reportProblem('OTP sign-in failed during account switch', e);
      }
    }
  }

  /**
   * Stop waiting for an account switch whose sign-in has not answered, and say so.
   *
   * The sign-in is still running, and the restart it needs is already attached to
   * it, so a late success still completes the switch. Reaching the limit itself
   * was reported by the bound. The alert is guarded because the whole switch flow
   * is started with `void`, so a throw here would reach nobody.
   */
  private async giveUpWaitingForAccountSwitchAsync(loader: DismissableLoader, newEmail: string): Promise<void> {
    try {
      await loader.dismissAsync();
      await this.env.showAccountSwitchUnfinishedAlertAsync(newEmail);
    } catch (err) {
      this.env.reportProblem('Showing the unfinished account switch message failed', err);
    }
  }

  /**
   * Email the user a replacement for their expired sign-in link, then tell them
   * what actually happened.
   *
   * The two halves have a `try` each, and keeping them apart is the whole point
   * of this method. They used to share one, so an email that failed to send took
   * the message announcing it down with it: the user was dropped back on the
   * screen they came from with nothing on it, and the only trace was a console
   * line nobody reads.
   *
   * The second `try` exists for a different reason. The one caller of the whole
   * account-switch flow starts it with `void` and never waits for it, so an
   * error thrown while putting a message on screen would become an unhandled
   * rejection and reach nobody. Report it instead.
   */
  private async replaceExpiredLinkAndSayWhatHappenedAsync(email: string): Promise<void> {
    let outcome: LinkResendOutcome;
    try {
      const resend = await this.env.resendMagicLinkAsync(email);
      // the server refuses to send when several accounts hold the address,
      // because no link can name the right one (#4505). Do not then tell the
      // person a fresh link is on its way — nothing was sent.
      outcome = resend?.accountAmbiguous ? LinkResendOutcome.AccountAmbiguous : LinkResendOutcome.Sent;
    } catch (resendErr) {
      console.error('App: Failed to resend magic link', resendErr);
      this.env.reportProblem('Replacing an expired sign-in link failed', resendErr);
      outcome = LinkResendOutcome.SendFailed;
    }

    try {
      switch (outcome) {
        case LinkResendOutcome.AccountAmbiguous:
          await this.env.showAmbiguousAccountAlertAsync();
          break;
        case LinkResendOutcome.SendFailed:
          await this.env.showLinkResendFailedAlertAsync(email);
          break;
        case LinkResendOutcome.Sent:
          await this.env.showLinkExpiredAlertAsync(email);
          break;
      }
    } catch (alertErr) {
      this.env.reportProblem('Showing the expired sign-in link message failed', alertErr);
    }
  }

  /**
   * Trade a purchase claim token for a sign-in token and use it.
   *
   * This never throws — on every caller the user can still sign in the ordinary
   * way if it does not work. But on three of the four callers a token was really
   * present, so a failure there means a paying customer's purchase did not reach
   * their account. Those failures are reported at PROBLEM tier, tagged with the
   * `source`, so somebody can eventually act on them. The one caller where a
   * failure means nothing — the install-referrer cold-start guess — stays silent.
   *
   * Answers whether the person is now signed in with the claimed account, or
   * whether the sign-in is still running past its wait limit — the sign-in is
   * bounded for every caller (df-28856ee737e9). The account switch needs that
   * answer: it restarts only on a finished sign-in, since a restart reloads the
   * page and would destroy one still running. The boot callers carry on either
   * way and ignore it. `onLateSuccess` is handed to the bound; see
   * `signInWithTokenBoundedAsync`.
   *
   * `onlyIfStillSignedOut` is for a caller that runs while the login page may
   * already be on screen: the late install-referrer answer (df-febb6fcfb9e9).
   * The exchange is a network round trip, and somebody may sign in by hand
   * during it. Signing in with the claimed account after that would replace the
   * account they just chose, so the check runs again right before the sign-in.
   */
  async exchangeClaimTokenAndSignInAsync(
    claimToken: string,
    source: ClaimTokenExchangeSource,
    onLateSuccess?: () => void,
    onlyIfStillSignedOut = false,
  ): Promise<ClaimTokenSignInOutcome> {
    try {
      const result = await this.env.exchangeClaimTokenAsync(claimToken);
      if (result.token && onlyIfStillSignedOut && this.env.currentUser()) {
        // the person signed in by other means while the exchange ran
        console.log('App: Claim token not used, somebody signed in meanwhile');
        return ClaimTokenSignInOutcome.NotSignedIn;
      }
      if (result.token) {
        // reaching the limit is already reported by the bound, and the sign-in
        // is still running — what matters to every caller is that this returns.
        const site: SignInTokenWaitSite = source === 'account-switch-expired-fallback'
          ? 'account-switch-claim-token'
          : 'claim-token';
        const outcome = await this.signInWithTokenBoundedAsync(result.token, site, onLateSuccess);
        if (outcome === SignInTokenWaitOutcome.StillWaiting) {
          return ClaimTokenSignInOutcome.StillWaiting;
        }
        console.log('App: Signed in via claim token');
        return ClaimTokenSignInOutcome.SignedIn;
      }
      if (result.error) {
        console.log(`App: Claim token exchange failed: ${result.error}`);
        this.reportClaimTokenExchangeFailure(source, { reason: result.error });
      }
      return ClaimTokenSignInOutcome.NotSignedIn;
    } catch (err) {
      console.log('App: Claim token exchange error (non-fatal)', err);
      this.reportClaimTokenExchangeFailure(source, { err });
      return ClaimTokenSignInOutcome.NotSignedIn;
    }
  }

  /**
   * Report a failed claim-token exchange, unless it came from the install-referrer
   * guess, where a failure is expected and means nothing.
   */
  private reportClaimTokenExchangeFailure(
    source: ClaimTokenExchangeSource,
    detail: { reason: string } | { err: unknown },
  ): void {
    if (source === 'install-referrer') return;
    this.env.reportProblem('Purchase claim token exchange failed', { source, ...detail });
  }

  /**
   * Hand the launch link's boot parameters to the boot that is already running,
   * if that is what this link is for. Answers whether it did.
   *
   * This MUST run before the Firebase auth init, which reads `signInToken`
   * straight out of the address bar. A link NOT carrying boot parameters is left
   * completely alone here, mark included, for the ordinary
   * `handleLaunchUrlOnceAsync` call later in the boot. Both rules, and what
   * breaks without them, are in design-docs/purchase-deep-linking.md,
   * "A cold-start link is read in place".
   */
  async adoptLaunchUrlBootParamsAsync(launchUrl: URL, currentPathAndQuery: string): Promise<boolean> {
    const decision = decideLaunchUrlHandling(
      launchUrl,
      currentPathAndQuery,
      this.env.readHandledLaunchUrl(),
    );
    if (decision.kind === 'skip') {
      console.log(`App: launch URL already dealt with (${decision.reason}), leaving it alone`);
      return false;
    }

    const action = decideDeepLinkAction(launchUrl, path => this.env.resolvePageName(path));
    if (action.kind !== 'reload-with-params') return false;

    // remember it BEFORE acting on it, for the same reason the reload path did:
    // the mark is what stops a later boot in this session acting on the same link
    // again, and #4994 was what happens when it is missing.
    this.env.markLaunchUrlHandled(launchUrl.toString());
    this.paramsAdoptedFromLaunchUrl = launchUrl.toString();
    this.env.adoptBootParamsInPlace(action.pathAndQuery);
    console.log('App: launch URL carries boot parameters — handed them to the boot already running');
    return true;
  }

  /**
   * Give up on the parameters a cold-start link carried, because the boot that
   * was going to read them has died.
   *
   * Only the fatal-error path calls this. In a healthy boot nothing needs to:
   * `adoptedBootParamsStillUnread` stops answering true once the parameters are
   * consumed, and the same link arriving after that reloads in the ordinary way.
   * A dead boot never consumes them, so without this the person's one natural
   * recovery — tapping the link again — would be swallowed in silence.
   */
  forgetAdoptedBootParams(): void {
    this.paramsAdoptedFromLaunchUrl = undefined;
  }

  /**
   * Act on the link that STARTED the app, at most once per web-view session.
   *
   * Use this for the cold-start launch link ONLY — a link tapped while the app is
   * already running goes straight to `handleDeepLinkAsync`, because tapping the
   * same link twice on purpose has to keep working. See `decideLaunchUrlHandling`.
   *
   * A link carrying boot parameters has normally been dealt with by
   * `adoptLaunchUrlBootParamsAsync` before this runs, and the mark it wrote makes
   * this a no-op.
   */
  async handleLaunchUrlOnceAsync(launchUrl: URL, currentPathAndQuery: string): Promise<void> {
    const decision = decideLaunchUrlHandling(
      launchUrl,
      currentPathAndQuery,
      this.env.readHandledLaunchUrl(),
    );

    if (decision.kind === 'skip') {
      console.log(`App: launch URL already dealt with (${decision.reason}), leaving it alone`);
      return;
    }

    // remember it BEFORE acting on it: acting on it can navigate the web view,
    // which tears this page down, so anything written afterwards is lost and the
    // next boot would act on the same link all over again
    this.env.markLaunchUrlHandled(launchUrl.toString());
    await this.handleDeepLinkAsync(launchUrl);
  }

  /** Act on a deep link the operating system handed us. */
  async handleDeepLinkAsync(url: URL): Promise<void> {
    // #5895: an onboarding link names the account it was sent to. Read it before anything
    // else and before any await, so on a cold start it is held by the time the boot checks
    // it. Whatever the link then does (usually open the login page) is unchanged.
    this.env.noteAccountHint(readAccountHintFromUrl(url));

    const action = decideDeepLinkAction(url, path => this.env.resolvePageName(path));

    switch (action.kind) {
      case 'reload-with-params':
        if (this.paramsAdoptedFromLaunchUrl === url.toString() && this.env.adoptedBootParamsStillUnread()) {
          // the second delivery of a cold-start link on iOS. The boot already has
          // these parameters and has not read them yet, so reloading would throw
          // that boot away mid-sign-in for nothing. Once they HAVE been read the
          // condition stops holding, so a deliberate re-tap still reloads.
          console.log('App: deep link carries boot parameters this boot has already taken, leaving it alone');
          return;
        }
        this.env.navigateWithinAppTo(action.pathAndQuery);
        return;

      case 'open-page':
        console.log(`App: Deep link navigating to ${action.pageName}`);
        await this.env.navigateToPageAsync(action.pageName, action.params);
        return;

      case 'complete-community-login':
        // no address is logged here, and none may be added: the pair this
        // carries IS a forum login for this person (#4525).
        console.log('App: Deep link is a community sign-in, completing it');
        await this.env.completeCommunityLoginAsync(action.sso, action.sig);
        return;

      case 'enter-coach-client':
        // no address is logged: the query names a client's account id
        console.log('App: Deep link is a coach notification link, entering the client account');
        await this.env.enterCoachClientAsync(action.clientUid, action.page);
        return;

      case 'open-client-page':
        console.log('App: Deep link is a client notification link, opening the page it names');
        await this.env.openClientPageAsync(action.page);
        return;

      case 'open-external':
        // unrecognized path (e.g. /privacy.html) — open in external browser
        console.log(`App: Unrecognized deep link path "${url.pathname}", opening in browser`);
        try {
          await this.env.openExternalLinkAsync(url.toString());
        } catch (err) {
          this.env.reportProblem('App: Failed to open deep link in browser', {
            code: (err as any)?.code,
            message: (err as any)?.message ?? String(err),
          });
        }
        return;

      case 'ignore':
        return;
    }
  }
}
